export const privacyDocument = {
  title: "Privacy Policy",
  rows: [
    [
      {
        sections: [
          {
            heading: "Who we are",
            blocks: [
              {
                type: "paragraph",
                lines: [
                  "Managed by: Asasiasa",
                  "Contact Email: contact@asasiasa.com",
                ],
              },
            ],
          },
          {
            heading: "What Data We Collect",
            blocks: [
              {
                type: "list",
                items: [
                  "Account Information: Email address, display name, public display name, password (encrypted), account status and plan tier",
                  "Content: Lessons, notes, flashcards, edit history, and publication status. This content is processed and stored to provide the service, but is private.",
                  "Public Profile: Public display name, bio, interests and visibility setting. New accounts receive a random public display name such as Learner 1234 by default.",
                  "Communities: Owned communities, memberships, invites, join requests, join code settings, listing/discover settings, shared lessons, community board content and community notifications",
                  "Connections: One to one connection status, private notes you send or receive, hidden thread state, local deletion state and plan-limit counters",
                  "Preferences: Theme settings, Chime on/off, Info bubble on/off, Notes & Flashcards Speed.",
                  "Plan Information: Your current plan tier (Free or Early Access)",
                  "Technical Data: IP address and request metadata processed by the app or infrastructure providers, including browser/device information, pages visited, timestamps, session data, security events and aggregated usage statistics",
                ],
              },
            ],
          },
          {
            heading: "How We Use Your Data",
            blocks: [
              {
                type: "list",
                items: [
                  "Provide and maintain the Asasiasa service",
                  "Authenticate your account and manage sessions",
                  "Store and display your lessons, notes, and flashcards",
                  "Enable public profile discovery when you choose to make your profile visible",
                  "Enable community discovery, membership, join by community code, direct or approval-based joining, community public pages and community notifications",
                  "Enable one to one connections and private notes, including plan limits and daily note limits synchronized across places where notes can be sent",
                  "Customize your experience (themes, preferences)",
                  "Communicate important service updates and account security notices such as password, email, deactivation, reactivation and deletion changes",
                  "Monitor security, prevent abuse, debug errors, measure performance, understand aggregated usage patterns and improve public/editorial content",
                ],
              },
            ],
          },
          {
            heading: "Cookies & Local Storage",
            blocks: [
              {
                type: "paragraph",
                text: "Asasiasa uses essential cookies and local storage to keep the service working correctly.",
              },
              {
                type: "list",
                items: [
                  "Maintain authentication, session security, and sign-in state",
                  "Store study progress, bookmarks, and reading preferences",
                  "Remember interface choices such as theme, speed, chime, and info-bubble settings",
                  "Show the privacy consent status required to continue using the app",
                ],
              },
            ],
          },
          {
            heading: "Hosting, Security & Analytics Providers",
            blocks: [
              {
                type: "paragraph",
                text: "Asasiasa may use hosting, CDN, infrastructure, logging and analytics providers such as AWS or Cloudflare. These providers may process technical request data, including IP address, browser/device information, pages visited, timestamps and security/performance data.",
              },
              {
                type: "paragraph",
                tone: "secondary",
                text: "Asasiasa uses this information for security, diagnostics, performance, abuse prevention and aggregated usage statistics. Public/editorial lesson, note and flashcard views may be counted in aggregate and are not intended to identify individual visitors.",
              },
            ],
          },
          {
            heading: "Public & Shared Content",
            blocks: [
              {
                type: "list",
                items: [
                  "You can make lessons public or share them via links",
                  "Your public profile can show your public display name, bio and interests when visibility is on. When visibility is off, public pages keep only the limited identity needed for community/member context.",
                  "Community listings and community public pages can show community name, description, owner/member public names and public community state, depending on community settings.",
                  "Community content is visible to community members. Owned communities can be listed in Discover or joined by community code, and can use direct join or approval requests.",
                  "Connection notes are private to the two participants and are not public community content.",
                  "You control whether your content is public, private, shared by link or shared inside a community.",
                ],
              },
            ],
          },
        ],
      },
      {
        sections: [
          {
            heading: "Your Rights",
            blocks: [
              {
                type: "paragraph",
                text: "You have the right to:",
              },
              {
                type: "list",
                spaced: true,
                items: [
                  {
                    label: "Export Your Data:",
                    text: "Download your account data, public profile settings, user-created content, owned community details, connection summaries and the connection notes written by you in TXT format. Notes written by other people are summarized as counts only.",
                  },
                  {
                    label: "Delete Your Account:",
                    text: "Permanently remove your account and the social/content data described in the delete confirmation modal",
                  },
                  {
                    label: "Update Information:",
                    text: "Modify your profile and preferences at any time",
                  },
                  {
                    label: "Contact Us:",
                    text: "Ask questions about your data or privacy",
                  },
                ],
              },
            ],
          },
          {
            heading: "Data Security",
            blocks: [
              {
                type: "paragraph",
                tone: "secondary",
                text: "We implement appropriate security measures to protect your personal data:",
              },
              {
                type: "list",
                items: [
                  "Password encryption using industry-standard methods",
                  "Secure HTTPS connections for all data transmission",
                  "Regular security updates and monitoring",
                  "Access controls limiting who can view your data",
                ],
              },
            ],
          },
          {
            heading: "Data Retention",
            blocks: [
              {
                type: "paragraph",
                text: "We retain your personal data only as long as necessary to provide our services. Self-deactivation preserves your account, content, memberships and connections so it can be reversed. Account deletion is permanent and removes the account and related social/content data described in the delete confirmation modal.",
              },
            ],
          },
        ],
      },
    ],
  ],
};

export const termsDocument = {
  title: "Terms of Use",
  rows: [
    [
      {
        sections: [
          {
            heading: "Self-Service Philosophy",
            blocks: [
              {
                type: "paragraph",
                tone: "secondary",
                text: "All account and content management is self-service. You have complete control over your data without requiring support or admin approval.",
              },
              {
                type: "paragraph",
                text: "By using Asasiasa, you also agree to the Privacy Policy and to the use of essential cookies and local storage required for authentication, security, and core app functionality.",
              },
              {
                type: "paragraph",
                text: "Asasiasa may use technical logs, infrastructure providers and aggregated usage statistics to operate, secure, troubleshoot, measure and improve the service, including public/editorial content.",
              },
            ],
          },
          {
            heading: "Legal Compliance & Code of Honor",
            blocks: [
              {
                type: "paragraph",
                tone: "secondary",
                text: "By creating an account or using Asasiasa, you agree to use the service only for lawful purposes and to comply with all applicable laws, regulations, and third-party rights.",
              },
              {
                type: "paragraph",
                text: "You must not use Asasiasa to commit, assist, promote, organize, request, conceal, or encourage illegal activity, or to post, store, or share content that is unlawful.",
              },
              {
                type: "paragraph",
                text: "You also agree to follow the Asasiasa Code of Honor:",
              },
              {
                type: "list",
                items: [
                  "Use Asasiasa for learning, study, research, reflection, and constructive community activity",
                  "Respect other people and do not harass, threaten, impersonate, exploit, defraud, spam, or intentionally mislead them",
                  "Do not upload, create, share, or request content that violates intellectual property rights, privacy rights, or another person's safety",
                  "Do not use lessons, notes, flashcards, communities, profiles, connections, or messages to distribute malicious, abusive, hateful, sexually exploitative, or harmful content",
                  "Keep public profile details, community names, community descriptions, shared lessons, and community board posts honest, appropriate, and lawful",
                  "Report or avoid misuse instead of amplifying it",
                ],
              },
              {
                type: "paragraph",
                tone: "secondary",
                className: "legal-document-paragraph-after-list",
                text: "If you violate these Terms, the Code of Honor, or applicable law, Asasiasa may block your content or suspend your account",
              },
            ],
          },
          {
            heading: "Account Management",
            blocks: [
              {
                type: "list",
                items: [
                  "Profile: You can update your display name and theme preferences",
                  "Security: Change password and email address",
                  "Public Profile: Edit your public display name, bio, interests and public visibility",
                  "Deactivation: Temporarily freeze your app access, hide public/social visibility and preserve your account data for reactivation",
                  "Reactivation: Personally restore your deactivated account anytime",
                  "Deletion: Permanently delete your account and the content/social data described in the delete modal",
                ],
              },
            ],
          },
          {
            heading: "Content Management",
            blocks: [
              {
                type: "list",
                items: [
                  "Creation: You can create lessons, notes, and flashcards",
                  "Editing: Full control over your content with edit history tracking",
                  "Visibility: Make content private, public, shared by link or community-shared",
                  "Deletion: Permanently remove your content at any time",
                ],
              },
              {
                type: "paragraph",
                tone: "secondary",
                className: "legal-document-paragraph-after-list",
                lines: [
                  "The mobile experience is optimized for portrait orientation; landscape usage is not fully supported.",
                  "Notes and flashcards can be created from inside a lesson only on desktop layouts, not from mobile layouts.",
                ],
              },
            ],
          },
        ],
      },
    ],
    [
      {
        sections: [
          {
            heading: "User Control & Ownership",
            blocks: [
              {
                type: "paragraph",
                text: "You own your content and only you can:",
              },
              {
                type: "list",
                spaced: true,
                items: [
                  "Create and edit your lessons, notes, and flashcards",
                  "Edit your account profile",
                  "Edit your public profile and decide whether it is visible",
                  "Create, list, archive, restore and delete communities you own",
                  "Hide, unhide or disconnect connections available to you",
                  "Delete your content",
                  "Delete your account and data",
                ],
              },
              {
                note: true,
                text: "New lessons are set to Private by default.",
              },
              {
                type: "paragraph",
                tone: "secondary",
                text: "Content in Asasiasa is lesson-based: the lesson is the parent unit, and notes, flashcards, and bookmarks are all linked to it. Deleting a lesson permanently removes all its associated notes, flashcards, and bookmarks.",
              },
            ],
          },
          {
            heading: "Admin Limitations",
            blocks: [
              {
                type: "paragraph",
                text: "Admins have limited powers and cannot:",
              },
              {
                type: "list",
                items: [
                  "Create your content",
                  "Edit your content",
                  "Delete your content",
                  "Delete your account",
                  "View or access your private content. Private content includes lessons and their notes and flashcards. Community content is also considered private content, meaning it is intended only for members of that community.",
                  {
                    text: "New lessons are set to Private by default.",
                    note: true,
                  },
                ],
              },
              {
                type: "paragraph",
                tone: "secondary",
                className: "legal-document-paragraph-after-list",
                text: "These admin limitations describe ordinary self-service content management. They do not limit safety, anti-abuse, legal compliance, or Terms enforcement actions described in Legal Compliance & Code of Honor.",
              },
            ],
          },
        ],
      },
    ],
    [
      {
        fullWidth: true,
        sections: [
          {
            heading: "Free Features",
            blocks: [
              {
                type: "list",
                items: [
                  "Dark Mode: Toggle between light and dark themes using the moon/sun button in the header",
                  "Reading Mode: Control interface visibility with the reading button for distraction-free reading",
                  "Lessons, Notes, and Flashcards: Create, edit, and study your content in dedicated flows.",
                  "Highlights: Mark key passages while reading.",
                  "Bookmarks: Save your place and return to where you left off.",
                  "Study Views: Read, take notes, and review flashcards in their own study modes.",
                  "Share Links: Generate shareable links for lessons.",
                  "Public Profile: Use a random public display name by default, edit your bio and interests, and control public profile visibility.",
                  "Communities: Create or join communities, list communities in Discover, join by community code, use direct or approval-based join requests, manage owner/member visibility and share lessons with your community.",
                  "Community Board: Community owners can publish board updates and announcements for their members.",
                  "Community Public Pages: Listed or accessible communities can have public community views while member-only content remains restricted.",
                  "Community Notifications: Get invite, request, join, leave, kick, paused and reactivated notifications inside the app.",
                  "Connections: Send private one to one notes, request/accept connections, hide or unhide threads and disconnect accepted connections.",
                  "Folders: Organize your lessons, notes, and flashcards into folders.",
                  "Export Data: Download your account data, public profile settings, lessons, notes, flashcards, owned community details, and privacy-safe community/connection summaries.",
                ],
              },
            ],
          },
          {
            heading: "Free Features Limitations",
            blocks: [
              {
                type: "list",
                items: [
                  "Lesson title max 80 characters",
                  "Author name max 50 characters (when used)",
                  "Lesson body max 40,000 characters (approximately 6,000-6,700 words)",
                  "Note body max 500 characters (approximately 70-80 words)",
                  "Flashcard question max 120 characters (approximately 18-20 words)",
                  "Flashcard answer max 180 characters (approximately 25-30 words)",
                  "Total content across all lesson, note, and flashcard bodies max 180,000 characters per user (approximately 27,000-30,000 words)",
                  "Display name max 50 characters",
                  "Public display name max 50 characters",
                  "Public profile bio max 1,000 characters",
                  "Public profile interests max 120 characters",
                  "Community name max 80 characters",
                  "Community description max 240 characters",
                  "Free users can own max 1 active community",
                  "Connections: max 250 characters per note, 2 notes per conversation per day, 4 notes per accepted connection per day, 20 visible connection threads, and 20 recent notes per thread. The daily limit is shared across public profile, Meet People, community profile and connection-detail note entry points.",
                  "Community share code is 6 characters (available in your Profile when sharing/inviting)",
                  "Notes and flashcards can currently be created on desktop layouts only",
                  "Contact form: name 1-100, subject 1-200, message 10-2000",
                  "Request body size limit: 2MB",
                ],
              },
            ],
          },
          {
            heading: "Early Access Plan",
            blocks: [
              {
                type: "paragraph",
                text: "Early Access is a free upgrade available to all users during the early access period. It includes higher content limits while keeping all Free features.",
              },
              {
                type: "list",
                items: [
                  "Lesson body max 200,000 characters (approximately 30,000-33,000 words)",
                  "Total content max 2,000,000 characters per user",
                  "Up to 10 active communities",
                  "Connections: max 500 characters per note, 5 notes per conversation per day, 10 notes per accepted connection per day, 500 visible connection threads, and 500 recent notes per thread",
                  "Note and flashcard creation limits remain the same as Free",
                  "You can upgrade or cancel Early Access at any time from your Profile or the Plans page",
                  "Cancelling Early Access reverts to Free limits; existing content is preserved but new content must respect Free limits",
                  "Early Access is free during the early access period and may become a paid plan in the future",
                ],
              },
            ],
          },
        ],
      },
    ],
    [
      {
        fullWidth: true,
        sections: [
          {
            heading: "Deleting Your Account",
            blocks: [
              {
                type: "paragraph",
                tone: "secondary",
                text: "Account deletion is permanent and irreversible. Consider deactivating first - deactivation is reversible and safer than permanent deletion.",
              },
              {
                type: "list",
                items: [
                  "Deletes your account immediately",
                  "Deletes all your lessons, notes, flashcards and saved content",
                  "Deletes communities you own, including their memberships, shared lessons, community board content and community notifications",
                  "Removes you from communities owned by other people",
                  "Deletes your connections, connection requests and one to one conversations for everyone involved",
                  "Removes your public profile from Meet People and public profile pages",
                  "Cannot be recovered or undone",
                  "Takes effect immediately and you will be logged out",
                  "Consider exporting your data first from the Privacy section",
                ],
              },
            ],
          },
          {
            heading: "Deactivating Your Account",
            blocks: [
              {
                type: "paragraph",
                tone: "secondary",
                text: "Deactivation temporarily freezes and hides your account. It is fully reversible - reactivation restores visibility and access.",
              },
              {
                type: "list",
                items: [
                  "Your app access will be frozen until reactivation",
                  "You can only use Profile to reactivate, export data, delete your account or logout",
                  "Your public lessons, notes and flashcards are hidden",
                  "Your public profile and Meet People visibility are hidden",
                  "Communities you own are hidden from Discover, join links and public community pages",
                  "Communities you own are paused, members see a paused notice and community paused/reactivated notifications are sent",
                  "Communities you are a member of show you as Self-deactivated in member lists",
                  "Connections and conversations are preserved, but your one to one notes are paused while either member is deactivated",
                  "All your content and relationships are preserved",
                  "Active plan subscriptions are not paused or extended during deactivation - your subscription cycle continues with no refund or recovery",
                  "This is reversible - reactivation restores visibility",
                ],
              },
            ],
          },
        ],
      },
    ],
  ],
};
